import { Property } from '@propertypal/shared/src/types/property';
import PropertyNotFound from '@propertypal/web-ui/src/property/PropertyNotFound';
import { useRouter } from 'next/router';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import PropertyDetails from '../components/property/PropertyDetails';
import useGpt from '../hooks/useGptSlot';
import { setTargeting } from '../services/ads/googletag';
import getProperty from '../services/property';
import { requireAuthToken } from '../utils/auth';

interface Props {
  published: boolean;
  property: Property;
}

export const getServerSideProps = requireAuthToken(async (ctx, store) => {
  const id = ctx.query.id as string;
  const address = ctx.query.address as string;
  const [property, published] = await getProperty(id, store.dispatch, ctx);

  if (property.path && (!address || `/${address}/${id}` !== property.path)) {
    const params = ctx.resolvedUrl && ctx.resolvedUrl.split('?')[1];
    const paramsStr = params ? `?${params}` : '';

    return {
      redirect: {
        destination: `${property.path}${paramsStr}`,
        permanent: true,
      },
    };
  }

  ctx.res.statusCode = published ? 200 : 404;

  return {
    props: {
      published,
      property,
    },
  };
});

const PropertyPage: FunctionComponent<Props> = (props) => {
  const router = useRouter();
  const dispatch = useDispatch();
  const [property, setProperty] = useState<Property>(props.property);

  const updateProperty = async () => {
    if (typeof router.query.id === 'string') {
      const [data] = await getProperty(router.query.id, dispatch);

      setProperty(data);
    }
  };

  useEffect(() => {
    // next.js has bug where getServersideProps will not always be called
    // https://github.com/vercel/next.js/issues/34365
    // The following logic checks for a bad route and manually re-fetches the property details
    if (!router.asPath.includes(property?.pathId)) {
      updateProperty();
    }
  }, [router.asPath]);

  useEffect(() => {
    setProperty(props.property);
  }, [props.property]);

  useGpt(() => {
    setTargeting({
      Category: property?.propertyType?.key,
      SaleType: property?.saleType?.key,
      Postcode: [property?.postcode],
    });
  }, [property?.id]);

  if (!props.published) {
    return <PropertyNotFound property={property} />;
  }

  return <PropertyDetails key={property.id} property={property} />;
};

export default React.memo(PropertyPage);
